import { EGardenPlantId, EItemId } from '../enums';
import { IReward, IRewardItem, ItemRarityEnum } from '../inventory/item-dto.interface';
import { ItemTypeEnum } from '../inventory/item-type.constant';

export interface IMissionRewardItem {
  correct: number;
  rewardItem: EItemId | null;
  rewardItemCount: number;
  rarity?: ItemRarityEnum;
  type?: ItemTypeEnum;
}

export interface IMissionRewardConfigItem {
  missionSizeMax: number;
  rewardTiers: IMissionRewardItem[];
}

export interface IMissionRewardPreviewItemConfig {
  rewardItem: string;
  count: number;
}

export enum MissionSizesEnum {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large'
}

export type IMissionRewardsPreviewConfig = Record<MissionSizesEnum, IMissionRewardPreviewItemConfig[]>;

export interface IMissionRewardsConfig {
  missionRewardsPreview: IMissionRewardsPreviewConfig;
  missionRewards: {
    verified: IMissionRewardConfigItem[];
    unverified: IMissionRewardConfigItem[];
  };
}

export interface IGardenMissionRewardsConfig {
  missionRewards: {
    verified: IMissionRewardConfigItem[];
    unverified: IMissionRewardConfigItem[];
  };
}

// Core Garden Plant Configuration (server-side)
export interface GardenPlant {
  plantId: EGardenPlantId;
  harvestCycleDays: number; // typically 7 or 14
  maxHarvests: number;
  harvestReward: IReward;
  celebrationBonusReward: IReward; // Threshold hit count X this variable
  seedName: string;

  // TEMP: Asset paths pending FE TDD
  seedAsset: string;
  fruitAsset: string;
  vineAsset: string; // not sure if decided on separate vine, but can support

  // Plant sprite assets for different states
  gardenAssets: {
    base: string;
    retired: string;
    stages: {
      notReady: string[];
      ready: string[];
    };
  };
}

// Garden Plant Configuration with enriched rewards (sent to client)
export interface GardenPlantEnriched {
  plantId: EGardenPlantId;
  harvestCycleDays: number;
  maxHarvests: number;
  harvestReward: IRewardItem;
  celebrationBonusReward: IRewardItem;
  seedName: string;
  seedAsset: string;
  fruitAsset: string;
  vineAsset: string;
  gardenAssets: {
    base: string;
    retired: string;
    stages: {
      notReady: string[];
      ready: string[];
    };
  };
}

// Garden Mission Configuration (server-side)
export interface GardenMissionsConfig {
  plants: Record<EGardenPlantId, GardenPlant>;
  harvestWindowStartDay: number;
  harvestWindowStartHour: number;
  harvestWindowStartMinute: number;
  harvestWindowEndDay: number;
  harvestWindowEndHour: number;
  harvestWindowEndMinute: number;
  missionRewards: IGardenMissionRewardsConfig;
}

// Garden Mission Configuration with enriched rewards (sent to client)
export interface GardenMissionsConfigEnriched {
  plants: Record<EGardenPlantId, GardenPlantEnriched>;
  harvestWindowStartDay: number;
  harvestWindowStartHour: number;
  harvestWindowStartMinute: number;
  harvestWindowEndDay: number;
  harvestWindowEndHour: number;
  harvestWindowEndMinute: number;
  missionRewards: IGardenMissionRewardsConfig;
}

// Garden Mission Entity
export interface GardenMission {
  classId: string;
  missionId: string;
  rewards: IReward[];
  dueDate: number; // UTC timestamp in milliseconds
  celebrationReward: IReward;
  finished: boolean; // all students assigned have completed their mission
}

// User's Garden Mission Progress
export interface GardenUserMission {
  missionId: string;
  plantId: EGardenPlantId;
  progressCollected: number[]; // an array holding index of collected rewards, 0th index is the completion reward
  celebrated: boolean; // celebration processing completed for this user and mission
}

export interface GardenUserMissionRich extends GardenUserMission {
  rewards: IRewardItem[];
  classCompleteAttempts: boolean; // based on allCompleted in GardenMission entity
  numQuestions: number; // total number of questions in the mission
  numQuestionsCompleted: number; // number of questions completed by the user
  numQuestionsCorrect: number; // number of questions correct by the user
}

// Class Garden Plant Instance
export interface ClassGardenPlant {
  id: number;
  missionId: string;
  userId: string;
  plantId: EGardenPlantId;
  thresholdHit?: number; // Used for celebration cutscene
  harvests: number;
  active: boolean;
  assetUrl: string; // URL for the sprite to display based on state
  postHarvestAssetUrl?: string; // URL for the sprite after harvest (only set when plant is ready to harvest)
  celebrationAssetUrl: string; // URL for the sprite used in celebration animations
}

// Nova Classmate Information
export interface NovaClassmate {
  userId: string;
  nickName: string;
  avatar: string;
}

// Core Classmate Information
export interface CoreClassmate {
  userId: string;
  name: string; // firstname + first letter of lastname
}

export interface HarvestResult {
  classGardenPlants: ClassGardenPlant[];
  checkHarvest: CheckHarvestResult;
}

export interface CheckHarvestResult {
  harvestAvailable: boolean;
  nextHarvestable: number;
  harvestRewardAmount: number;
  harvestableCount: number;
  closestActivePage?: number;
}

// Garden Visit Data
export interface GardenVisit {
  classGardenPlants: ClassGardenPlant[];
  harvestAvailable: boolean;
  nextHarvestable: number; // timestamp
  harvestRewardAmount: number;
  totalPlanted: number;
  closestActivePage?: number;
}

export interface GardenVisitLite {
  classGardenPlants: ClassGardenPlant[];
}

export interface GardenMissionClaimRewardResult {
  gardenUserMission: GardenUserMission;
  reward: IRewardItem;
}

export interface GardenMissionCelebrateResult {
  gardenUserMission: GardenUserMission;
  baseReward: IRewardItem;
  multiplier: number;
  finalReward: IRewardItem;
  completionComboRewarded: boolean;
  novaUsers: { userId: string; avatarURL: string }[];
  plantsForCelebration: ClassGardenPlant[];
}

export interface GardenMissionsCelebrateResult {
  results: GardenMissionCelebrateResult[];
  failedMissionIds: string[];
}

export interface GardenUserMissionsList {
  startedGardenMissions: GardenUserMissionRich[];
  notStartedGardenMissionIds: string[];
  config: GardenMissionsConfigEnriched;
}
